// BookingEditDialog.tsx
// Dialog zum Bearbeiten einer bestehenden Buchung

import React, { useState, useEffect, useMemo } from 'react';
import {
  XMarkIcon,
  UserGroupIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlusCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { adminService, BookingAddOnDto, CalculatedAddonDto, AddOnService, BookingCustomItem } from '../../services/adminService';
import { calculateTieredPrice, calculateCategoryTourPrice, TourTypeWithPriceTiers, ParticipantCategoryCounts } from '../../utils/pricingUtils';
import { API_BASE_URL } from '../../config/api.config';
import './BookingEditDialog.css';

// Interface für bearbeitbare AddOns mit Kategorisierungs-Info
interface EnrichedAddOn extends BookingAddOnDto {
  originalQuantity: number;
  isModified: boolean;
  isNew?: boolean;
  // Kategorisierungs-Felder aus CalculatedAddonDto
  isOptionalChoice: boolean;
  isAccommodation: boolean;
  bedsPerUnit?: number;
  quantityFree: number;
  quantityPaid: number;
  isIncludedInTourPrice: boolean;
  grossPricePerUnit: number;
  quantityMultiplier: number; // Multiplikator für optionale AddOns (z.B. 2 = 2× Teilnehmerzahl)
}

// Booking status options for dropdown
const BOOKING_STATUS_OPTIONS = [
  { value: 'Draft', label: 'Entwurf' },
  { value: 'Option', label: 'Option (vorläufig)' },
  { value: 'Confirmed', label: 'Bestätigt' },
  { value: 'PaymentPending', label: 'Zahlung ausstehend' },
  { value: 'AwaitingFinalPayment', label: 'Warte auf Restzahlung' },
  { value: 'PartiallyPaid', label: 'Teilweise bezahlt' },
  { value: 'FullyPaid', label: 'Vollständig bezahlt' },
  { value: 'InProgress', label: 'In Bearbeitung' },
  { value: 'Completed', label: 'Abgeschlossen' },
  { value: 'Cancelled', label: 'Storniert' },
];

interface Booking {
  id: string;
  bookingNumber: string;
  customerId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  tripDate: string;
  tripTime: string;
  tripType: string;
  participantCount: number;
  status: string; // Changed to string to support all backend statuses
  paymentStatus: 'pending' | 'partial' | 'paid' | 'refunded';
  totalAmount: number;
  paidAmount: number;
  notes: string[];
  createdAt: string;
  updatedAt: string;
  extras?: string[];
  specialRequests?: string;
  quoteValidUntil?: string; // Stichtag für Options
  // Teilnehmer-Kategorien (Anlage v1.3 Abschnitt 2.1), optional fuer Abwaertskompatibilitaet
  adultCount?: number;
  childCount?: number;
  childSmallCount?: number;
  infantCount?: number;
  freeParticipantWithSeatCount?: number;
  freeParticipantNoSeatCount?: number;
  // Anzahlungssatz-Übersteuerung in Dezimalform (0.40 = 40%), Anlage v1.3 Abschnitt 2.5 Punkt 2
  depositPercentageOverride?: number;
}

interface BookingEditDialogProps {
  booking: Booking;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedBooking: Booking) => void;
}

// Hilfskomponente für einzelnes AddOn
interface AddonItemProps {
  addon: EnrichedAddOn;
  onQuantityChange: (addonId: number, newQuantity: number) => void;
  onRemove: (addonId: number) => void;
  formatCurrency: (amount: number) => string;
  disabled: boolean;
  showInclusive?: boolean;
}

const AddonItem: React.FC<AddonItemProps> = ({
  addon,
  onQuantityChange,
  onRemove,
  formatCurrency,
  disabled,
  showInclusive
}) => {
  const grossPrice = addon.grossPricePerUnit || addon.unitPrice * (1 + (addon.taxRate || 0.19));
  const isFullyInclusive = addon.quantityFree >= addon.quantity && addon.quantityPaid <= 0;
  const hasPartialInclusive = addon.quantityFree > 0 && addon.quantityPaid > 0;
  // Nur kostenpflichtiger Anteil wird berechnet
  const paidTotal = addon.quantityPaid * grossPrice;

  return (
    <div className={`addon-item ${addon.isModified ? 'addon-modified' : ''} ${addon.isNew ? 'addon-new' : ''}`}>
      <div className="addon-info">
        <span className="addon-name">{addon.serviceName}</span>
        {addon.categoryName && (
          <span className="addon-category">{addon.categoryName}</span>
        )}
        {showInclusive && isFullyInclusive && (
          <span className="addon-inclusive-badge">inklusive</span>
        )}
      </div>

      <div className="addon-quantity-control">
        <button
          type="button"
          onClick={() => onQuantityChange(addon.id, addon.quantity - 1)}
          disabled={disabled || addon.quantity <= 0}
          className="btn-quantity"
        >
          -
        </button>
        <input
          type="number"
          value={addon.quantity}
          onChange={(e) => onQuantityChange(addon.id, parseInt(e.target.value) || 0)}
          className="quantity-input"
          min="0"
          disabled={disabled}
        />
        <button
          type="button"
          onClick={() => onQuantityChange(addon.id, addon.quantity + 1)}
          disabled={disabled}
          className="btn-quantity"
        >
          +
        </button>
      </div>

      <div className="addon-price">
        <span className="price-per-unit">{formatCurrency(grossPrice)}/Stk.</span>
        {isFullyInclusive ? (
          <span className="price-total inclusive">Komplett inklusive</span>
        ) : hasPartialInclusive ? (
          <>
            <span className="price-breakdown">
              {addon.quantityFree} Stk. inklusive, {addon.quantityPaid} Stk. kostenpflichtig
            </span>
            <span className="price-total">{formatCurrency(paidTotal)}</span>
          </>
        ) : (
          <span className="price-total">{formatCurrency(paidTotal)}</span>
        )}
      </div>

      <button
        type="button"
        onClick={() => onRemove(addon.id)}
        disabled={disabled}
        className="btn-remove-addon"
        title="Entfernen"
      >
        ×
      </button>
    </div>
  );
};

const BookingEditDialog: React.FC<BookingEditDialogProps> = ({
  booking,
  isOpen,
  onClose,
  onSave
}) => {
  // Kategorien aus Buchung ableiten (Legacy-Buchungen ohne Kategorien: alle als Erwachsene)
  const deriveCategories = (b: Booking): ParticipantCategoryCounts => {
    const catSum = (b.adultCount ?? 0) + (b.childCount ?? 0) + (b.childSmallCount ?? 0) +
      (b.infantCount ?? 0) + (b.freeParticipantWithSeatCount ?? 0) + (b.freeParticipantNoSeatCount ?? 0);
    if (catSum === 0) {
      return { adults: b.participantCount, children6to12: 0, children3to5: 0, children0to2: 0, freeSeats: 0 };
    }
    return {
      adults: b.adultCount ?? 0,
      children6to12: b.childCount ?? 0,
      children3to5: b.childSmallCount ?? 0,
      children0to2: b.infantCount ?? 0,
      freeSeats: (b.freeParticipantWithSeatCount ?? 0) + (b.freeParticipantNoSeatCount ?? 0),
    };
  };

  // State
  const [participantCount, setParticipantCount] = useState(booking.participantCount);
  const [categories, setCategories] = useState<ParticipantCategoryCounts>(deriveCategories(booking));
  // FIX 24.08.2026 (Kundin-Befund 25.08., Inklusivleistungen): Anspruch auf
  // Getraenkeflat und Speise haben zahlende Gaeste UND Freiplaetze, NICHT die
  // nicht zahlenden Kinder 0-2 (Anlage v1.3 Abschnitt 2.1 Punkt 9).
  const mealRelevantCount = participantCount - categories.children0to2;
  const [tourTypesWithPricing, setTourTypesWithPricing] = useState<TourTypeWithPriceTiers[]>([]);
  // Anzahlungssatz-Übersteuerung in Prozent ('' = Standard des Tour-Typs)
  const [depositOverridePct, setDepositOverridePct] = useState<string>(
    booking.depositPercentageOverride ? String(Math.round(booking.depositPercentageOverride * 100)) : ''
  );
  // Blankopositionen (Anlage v1.3 Abschnitt 2.5 Punkt 8)
  const [customItems, setCustomItems] = useState<BookingCustomItem[]>([]);
  const [customItemsDelta, setCustomItemsDelta] = useState<number>(0); // Preisänderung seit Dialog-Öffnung
  const [newItemText, setNewItemText] = useState('');
  const [newItemPrice, setNewItemPrice] = useState('');
  const [newItemQty, setNewItemQty] = useState('1');
  const [newItemTaxRate, setNewItemTaxRate] = useState('0.19');
  const [savingCustomItem, setSavingCustomItem] = useState(false);
  const [availableCapacity, setAvailableCapacity] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [bookingStatus, setBookingStatus] = useState(booking.status || 'Confirmed');
  const [optionValidUntil, setOptionValidUntil] = useState(booking.quoteValidUntil || '');

  // AddOn State
  const [bookingAddons, setBookingAddons] = useState<EnrichedAddOn[]>([]);
  const [loadingAddons, setLoadingAddons] = useState(false);
  const [addonsError, setAddonsError] = useState<string | null>(null);

  // Dialog für neue AddOns
  const [showAddAddonDialog, setShowAddAddonDialog] = useState(false);
  const [availableAddons, setAvailableAddons] = useState<CalculatedAddonDto[]>([]);
  const [allAddOnServices, setAllAddOnServices] = useState<AddOnService[]>([]); // ALLE verfügbaren AddOns
  const [loadingAvailableAddons, setLoadingAvailableAddons] = useState(false);
  const [addonSearchTerm, setAddonSearchTerm] = useState(''); // Suchfeld für AddOn-Dialog
  const [addingAddon, setAddingAddon] = useState(false); // Loading-State beim Hinzufügen
  const [dialogError, setDialogError] = useState<string | null>(null); // Fehler im Dialog

  // Tour-Typen mit Staffelpreisen laden (fuer kategorie-bewusste Preisberechnung)
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/tour-types`);
        if (response.ok) {
          setTourTypesWithPricing(await response.json());
        }
      } catch (err) {
        console.warn('[BookingEditDialog] Tour-Typen mit Preisen konnten nicht geladen werden:', err);
      }
    })();
  }, [isOpen]);

  // Update state when booking changes (e.g., when dialog opens with different booking)
  useEffect(() => {
    setParticipantCount(booking.participantCount);
    setCategories(deriveCategories(booking));
    // Map status from API to dropdown value (handle case differences)
    const statusValue = BOOKING_STATUS_OPTIONS.find(
      opt => opt.value.toLowerCase() === (booking.status || '').toLowerCase()
    )?.value || booking.status || 'Confirmed';
    setBookingStatus(statusValue);
    // Convert ISO date to YYYY-MM-DD format for date input
    const validUntil = booking.quoteValidUntil;
    if (validUntil) {
      const dateOnly = validUntil.split('T')[0]; // Extract YYYY-MM-DD from ISO string
      setOptionValidUntil(dateOnly);
    } else {
      setOptionValidUntil('');
    }
  }, [booking]);

  // Load AddOns when dialog opens
  useEffect(() => {
    if (isOpen && booking.id) {
      loadBookingAddons();
      loadCustomItems();
    }
  }, [isOpen, booking.id]);

  // Blankopositionen laden (Anlage v1.3 Abschnitt 2.5 Punkt 8)
  const loadCustomItems = async () => {
    try {
      const bookingId = parseInt(booking.id);
      if (isNaN(bookingId)) return;
      const items = await adminService.getBookingCustomItems(bookingId);
      setCustomItems(items);
      setCustomItemsDelta(0);
    } catch (err) {
      console.warn('Blankopositionen konnten nicht geladen werden:', err);
    }
  };

  const handleAddCustomItem = async () => {
    const price = parseFloat(newItemPrice.replace(',', '.'));
    const qty = parseInt(newItemQty) || 1;
    if (!newItemText.trim()) {
      setError('Bitte einen Positionstext für die Blankoposition angeben');
      return;
    }
    if (isNaN(price) || price < 0) {
      setError('Bitte einen gültigen Preis (brutto) für die Blankoposition angeben');
      return;
    }
    setSavingCustomItem(true);
    setError(null);
    try {
      const bookingId = parseInt(booking.id);
      const created = await adminService.addBookingCustomItem(bookingId, {
        description: newItemText.trim(),
        quantity: qty,
        grossPrice: price,
        taxRate: parseFloat(newItemTaxRate)
      });
      setCustomItems(prev => [...prev, created]);
      setCustomItemsDelta(prev => prev + Math.round(price * qty * 100) / 100);
      setNewItemText('');
      setNewItemPrice('');
      setNewItemQty('1');
      setNewItemTaxRate('0.19');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Blankoposition konnte nicht angelegt werden');
    } finally {
      setSavingCustomItem(false);
    }
  };

  const handleDeleteCustomItem = async (item: BookingCustomItem) => {
    setSavingCustomItem(true);
    setError(null);
    try {
      const bookingId = parseInt(booking.id);
      await adminService.deleteBookingCustomItem(bookingId, item.id);
      setCustomItems(prev => prev.filter(ci => ci.id !== item.id));
      setCustomItemsDelta(prev => prev - Math.round(item.grossPrice * item.quantity * 100) / 100);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Blankoposition konnte nicht entfernt werden');
    } finally {
      setSavingCustomItem(false);
    }
  };

  const loadBookingAddons = async () => {
    setLoadingAddons(true);
    setAddonsError(null);
    try {
      const bookingId = parseInt(booking.id);
      if (isNaN(bookingId)) {
        throw new Error('Ungültige Buchungs-ID');
      }

      // 1. Bestehende Buchungs-AddOns laden
      const addons = await adminService.getBookingAddons(bookingId);

      // 2. CalculatedAddons für TourType laden (für Kategorisierungs-Info)
      let calculatedAddons: CalculatedAddonDto[] = [];
      try {
        calculatedAddons = await adminService.calculateAddonsForTourType(
          booking.tripType,
          // Inklusiv-Mengen ohne nicht zahlende Kinder 0-2 (FIX 24.08.2026)
          booking.participantCount - (booking.infantCount ?? 0)
        );
        setAvailableAddons(calculatedAddons);
      } catch (calcErr) {
        console.warn('Konnte CalculatedAddons nicht laden:', calcErr);
      }

      // 3. Merge: BookingAddons mit CalculatedAddon-Infos anreichern
      const enrichedAddons: EnrichedAddOn[] = addons.map(addon => {
        const calculated = calculatedAddons.find(c => c.addOnServiceId === addon.addOnServiceId);
        const grossPrice = addon.unitPrice * (1 + (addon.taxRate || 0.19));

        // Berechne quantityFree und quantityPaid basierend auf Teilnehmerzahl und isIncludedInTourPrice
        const isIncluded = calculated?.isIncludedInTourPrice || false;
        const maxFree = isIncluded ? booking.participantCount - (booking.infantCount ?? 0) : 0;
        const quantityFree = Math.min(addon.quantity, maxFree);
        const quantityPaid = Math.max(0, addon.quantity - quantityFree);

        return {
          ...addon,
          originalQuantity: addon.quantity,
          isModified: false,
          isNew: false,
          // Kategorisierungs-Felder
          isOptionalChoice: calculated?.isOptionalChoice || false,
          isAccommodation: calculated?.isAccommodation || false,
          bedsPerUnit: calculated?.bedsPerUnit,
          quantityFree,
          quantityPaid,
          isIncludedInTourPrice: isIncluded,
          grossPricePerUnit: calculated?.grossPricePerUnit || grossPrice,
          quantityMultiplier: calculated?.quantityMultiplier || 1
        };
      });

      setBookingAddons(enrichedAddons);
    } catch (err: any) {
      console.error('Fehler beim Laden der AddOns:', err);
      setAddonsError('AddOns konnten nicht geladen werden');
      setBookingAddons([]);
    } finally {
      setLoadingAddons(false);
    }
  };

  // Check availability when participant count changes
  useEffect(() => {
    checkAvailability();
  }, [participantCount]);

  const checkAvailability = async () => {
    setLoading(true);
    setError(null);
    setWarning(null);
    
    try {
      // TODO: Replace with real API call
      // const response = await adminService.checkSlotAvailability(booking.slotId);
      
      // Mock response
      const mockCapacity = 140;
      const mockCurrentBookings = 100;
      const available = mockCapacity - mockCurrentBookings;
      
      setAvailableCapacity(available);
      
      if (participantCount > available) {
        setWarning(`Achtung: Nur noch ${available} Plätze verfügbar. Buchung wird überbucht!`);
      }
    } catch (err) {
      console.error('Error checking availability:', err);
      setError('Verfügbarkeit konnte nicht geprüft werden');
    } finally {
      setLoading(false);
    }
  };

  // Handle participant count change (Differenz wird auf Erwachsene angewendet)
  const handleParticipantCountChange = (newCount: number) => {
    if (newCount < 1) return;
    if (newCount > 200) {
      setError('Maximale Teilnehmerzahl ist 200');
      return;
    }
    setParticipantCount(newCount);
    setCategories(prev => {
      const others = prev.children6to12 + prev.children3to5 + prev.children0to2 + prev.freeSeats;
      return { ...prev, adults: Math.max(0, newCount - others) };
    });
  };

  // Handle category change (Gesamtzahl folgt der Kategoriesumme)
  const handleCategoryChange = (key: keyof ParticipantCategoryCounts, value: number) => {
    const val = Math.max(0, value);
    setCategories(prev => {
      const next = { ...prev, [key]: val };
      const total = next.adults + next.children6to12 + next.children3to5 + next.children0to2 + next.freeSeats;
      if (total > 200) {
        setError('Maximale Teilnehmerzahl ist 200');
        return prev;
      }
      setParticipantCount(total);
      return next;
    });
  };

  // Kategorie-bewusste Basispreis-Differenz (Staffel NUR nach Erwachsenen, Kinder 60%/20%)
  // Liefert 0, wenn keine Staffelpreisdaten geladen werden konnten (dann bleibt der alte Basispreis)
  const calculateBasePriceDifference = (): number => {
    if (tourTypesWithPricing.length === 0) return 0;
    const originalCategories = deriveCategories(booking);
    const origTier = calculateTieredPrice(booking.tripType, originalCategories.adults, tourTypesWithPricing, 0);
    const newTier = calculateTieredPrice(booking.tripType, categories.adults, tourTypesWithPricing, 0);
    if (origTier.pricingMode === 'FixedPrice' || newTier.pricingMode === 'FixedPrice') return 0;
    if (origTier.pricePerPerson <= 0 || newTier.pricePerPerson <= 0) return 0;
    const originalBase = calculateCategoryTourPrice(origTier.pricePerPerson, originalCategories);
    const newBase = calculateCategoryTourPrice(newTier.pricePerPerson, categories);
    return newBase - originalBase;
  };

  // Handle AddOn quantity change mit Max-Validierung und dynamischer quantityFree/quantityPaid Berechnung
  const handleAddonQuantityChange = (addonId: number, newQuantity: number) => {
    if (newQuantity < 0) return;

    setBookingAddons(prev => prev.map(addon => {
      if (addon.id === addonId) {
        // Max-Validierung für "Auswahl pro Teilnehmer" (isOptionalChoice)
        // Max = participantCount × quantityMultiplier
        if (addon.isOptionalChoice) {
          const maxAllowed = mealRelevantCount * (addon.quantityMultiplier || 1);
          if (newQuantity > maxAllowed) {
            return addon; // Keine Änderung wenn über Max
          }
          // T-058 (Kundin-Befund 15.09.2026): SUMME aller Wahlgerichte darf die
          // leistungsberechtigte Teilnehmerzahl x Multiplikator nicht übersteigen
          // (wie AddonSelector bei der Online-Buchung). Reduzieren ist immer erlaubt.
          if (newQuantity > addon.quantity) {
            const sumOthers = prev
              .filter(a => a.isOptionalChoice && !a.isAccommodation && a.id !== addonId)
              .reduce((sum, a) => sum + a.quantity, 0);
            if (sumOthers + newQuantity > maxAllowed) {
              return addon; // Keine Änderung wenn Summe über Max
            }
          }
        }

        // Dynamische Berechnung von quantityFree und quantityPaid
        // Inklusivleistungen: nur bis zur leistungsberechtigten Anzahl kostenlos (FIX 24.08.2026)
        const maxFree = addon.isIncludedInTourPrice ? mealRelevantCount : 0;
        const quantityFree = Math.min(newQuantity, maxFree);
        const quantityPaid = Math.max(0, newQuantity - quantityFree);

        return {
          ...addon,
          quantity: newQuantity,
          quantityFree,
          quantityPaid,
          totalPrice: newQuantity * addon.unitPrice,
          isModified: newQuantity !== addon.originalQuantity
        };
      }
      return addon;
    }));
  };

  // Calculate AddOn total (Brutto) - NUR kostenpflichtige Mengen (quantityPaid), nicht Inklusivleistungen
  const calculateAddonTotal = (): number => {
    return bookingAddons.reduce((sum, addon) => {
      const grossPrice = addon.grossPricePerUnit || addon.unitPrice * (1 + (addon.taxRate || 0.19));
      // Nur quantityPaid berechnen (kostenpflichtige Extras), nicht quantityFree (inklusive)
      return sum + (addon.quantityPaid * grossPrice);
    }, 0);
  };

  // Calculate total price (Base + AddOns) - konsistent mit calculateAddonTotal (nur quantityPaid)
  const calculateTotalPrice = (): number => {
    // Basis-Preis aus der bestehenden Buchung (bereits im totalAmount enthalten)
    // Hier berechnen wir nur die Differenz durch AddOn-Änderungen
    const originalAddonTotal = bookingAddons.reduce((sum, addon) => {
      const grossPrice = addon.grossPricePerUnit || addon.unitPrice * (1 + (addon.taxRate || 0.19));
      // Original: quantityPaid basierend auf originalQuantity
      const origMaxFree = addon.isIncludedInTourPrice ? booking.participantCount : 0;
      const origQuantityPaid = Math.max(0, addon.originalQuantity - origMaxFree);
      return sum + (origQuantityPaid * grossPrice);
    }, 0);

    const newAddonTotal = calculateAddonTotal();
    const addonDifference = newAddonTotal - originalAddonTotal;

    // Basispreis-Differenz durch Kategorie-/Teilnehmeraenderungen (Staffel nach Erwachsenen)
    const baseDifference = calculateBasePriceDifference();

    // Blankopositionen: seit Dialog-Oeffnung hinzugefuegte/entfernte Positionen
    // (serverseitig bereits im Buchungspreis verbucht, booking.totalAmount ist aber noch der alte Stand)
    return booking.totalAmount + addonDifference + baseDifference + customItemsDelta;
  };

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  // Kategorisierungs-Logik
  const categorizedAddons = useMemo(() => {
    // Auswahl pro Teilnehmer (isOptionalChoice === true)
    const optionalChoice = bookingAddons.filter(a =>
      a.isOptionalChoice && !a.isAccommodation
    );

    // Inklusivleistungen (quantityPaid = 0, aber quantity > 0)
    const inclusive = bookingAddons.filter(a =>
      !a.isOptionalChoice && !a.isAccommodation &&
      a.quantityFree > 0 && a.quantityPaid <= 0
    );

    // Extras (kostenpflichtig oder noch nicht ausgewählt)
    const extras = bookingAddons.filter(a =>
      !a.isOptionalChoice && !a.isAccommodation &&
      (a.quantityPaid > 0 || (a.quantityFree === 0 && a.quantity > 0))
    );

    // Falls Kategorisierung nicht geladen wurde, alle als Extras anzeigen
    const uncategorized = bookingAddons.filter(a =>
      !optionalChoice.includes(a) && !inclusive.includes(a) && !extras.includes(a)
    );

    return {
      optionalChoice,
      inclusive,
      extras: [...extras, ...uncategorized]
    };
  }, [bookingAddons]);

  // T-058: Summe der Wahlgerichte gegen die leistungsberechtigte Teilnehmerzahl
  // (Multiplikator wie in AddonSelector vom ersten Wahlgericht)
  const optionalChoiceSummary = useMemo(() => {
    const list = categorizedAddons.optionalChoice;
    const total = list.reduce((sum, a) => sum + a.quantity, 0);
    const multiplier = list[0]?.quantityMultiplier || 1;
    const max = Math.max(0, mealRelevantCount) * multiplier;
    return { total, max, remaining: max - total, over: total > max, hasChoices: list.length > 0 };
  }, [categorizedAddons, mealRelevantCount]);

  // Verfügbare AddOns laden (für Dialog) - ALLE AddOns, nicht nur TourType-spezifische
  const loadAvailableAddons = async () => {
    setLoadingAvailableAddons(true);
    setAddonSearchTerm(''); // Suchfeld zurücksetzen
    setDialogError(null); // Fehler zurücksetzen
    try {
      // Lade ALLE aktiven AddOn-Services (als Admin kann man alle hinzufügen)
      const allServices = await adminService.getAllAddOns(false);
      console.log('Geladene AddOn-Services:', allServices.length);
      setAllAddOnServices(allServices);
      setShowAddAddonDialog(true);
    } catch (err) {
      console.error('Fehler beim Laden verfügbarer AddOns:', err);
      setError('Verfügbare AddOns konnten nicht geladen werden');
    } finally {
      setLoadingAvailableAddons(false);
    }
  };

  // Neues AddOn hinzufügen (akzeptiert AddOnService oder CalculatedAddonDto)
  const handleAddNewAddon = async (addon: AddOnService | CalculatedAddonDto) => {
    console.log('handleAddNewAddon aufgerufen mit:', addon);
    setDialogError(null);
    setAddingAddon(true);

    try {
      const bookingId = parseInt(booking.id);
      console.log('BookingID:', bookingId);

      // Ermittle die addOnServiceId je nach Typ
      const serviceId = 'id' in addon ? addon.id : addon.addOnServiceId!;
      console.log('ServiceID:', serviceId);

      // Mindestmenge aus dem AddOn lesen (falls vorhanden)
      const minQuantity = ('minQuantityPerBooking' in addon && addon.minQuantityPerBooking)
        ? addon.minQuantityPerBooking
        : 1;
      const initialQuantity = Math.max(1, minQuantity);
      console.log('Mindestmenge:', minQuantity, '-> Verwende:', initialQuantity);

      console.log('Rufe API auf: addBookingAddon...');
      const newAddon = await adminService.addBookingAddon(bookingId, {
        addOnServiceId: serviceId,
        quantity: initialQuantity,
        specialRequests: ''
      });
      console.log('API Antwort:', newAddon);

      // Ermittle Eigenschaften je nach Typ (AddOnService vs CalculatedAddonDto)
      const isAddOnService = 'id' in addon && !('addOnServiceId' in addon);
      const isOptionalChoice = isAddOnService ? (addon as AddOnService).isOptionalChoice || false : (addon as CalculatedAddonDto).isOptionalChoice;
      const isAccommodation = isAddOnService ? (addon as AddOnService).isAccommodation || false : (addon as CalculatedAddonDto).isAccommodation;
      const bedsPerUnit = isAddOnService ? (addon as AddOnService).bedsPerUnit : (addon as CalculatedAddonDto).bedsPerUnit;
      const grossPrice = isAddOnService ? (addon as AddOnService).grossPrice : (addon as CalculatedAddonDto).grossPricePerUnit;

      // Neu hinzugefügte AddOns sind standardmäßig nicht inklusive (außer es ist ein TourType-AddOn)
      const calcAddon = availableAddons.find(c => c.addOnServiceId === serviceId);
      const isIncludedInTourPrice = calcAddon?.isIncludedInTourPrice || false;

      // Zur lokalen Liste hinzufügen
      // Berechne quantityFree und quantityPaid basierend auf initialQuantity
      const qtyFree = isIncludedInTourPrice ? Math.min(initialQuantity, participantCount) : 0;
      const qtyPaid = Math.max(0, initialQuantity - qtyFree);

      const enrichedAddon: EnrichedAddOn = {
        ...newAddon,
        originalQuantity: 0,
        isModified: true,
        isNew: true,
        isOptionalChoice,
        isAccommodation,
        bedsPerUnit,
        quantityFree: qtyFree,
        quantityPaid: qtyPaid,
        isIncludedInTourPrice,
        grossPricePerUnit: grossPrice,
        quantityMultiplier: calcAddon?.quantityMultiplier || 1
      };

      console.log('EnrichedAddon erstellt:', enrichedAddon);
      setBookingAddons(prev => [...prev, enrichedAddon]);
      setShowAddAddonDialog(false);
      setAddonSearchTerm('');
    } catch (err: any) {
      console.error('Fehler beim Hinzufügen:', err);
      console.error('Error Response:', err.response?.data);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Fehler beim Hinzufügen der Zusatzoption';
      setDialogError(errorMessage);
    } finally {
      setAddingAddon(false);
    }
  };

  // AddOn entfernen
  const handleRemoveAddon = async (addonId: number) => {
    if (!window.confirm('Zusatzoption wirklich entfernen?')) return;

    try {
      await adminService.deleteBookingAddon(addonId);
      setBookingAddons(prev => prev.filter(a => a.id !== addonId));
    } catch (err: any) {
      console.error('Fehler beim Entfernen:', err);
      setError('Fehler beim Entfernen der Zusatzoption');
    }
  };

  const totalPrice = calculateTotalPrice();
  const priceDifference = totalPrice - booking.totalAmount;

  // Save changes - Backend API Integration
  const handleSave = async () => {
    // Validierung für Option-Status
    if (bookingStatus === 'Option' && !optionValidUntil) {
      setError('Bitte geben Sie einen Stichtag für die Option an.');
      return;
    }

    // T-058: Überzählige Wahlgerichte nicht speichern (sie wären "inklusive" und ohne Aufpreis)
    if (optionalChoiceSummary.hasChoices && optionalChoiceSummary.over) {
      setError(
        `Auswahl pro Teilnehmer: ${optionalChoiceSummary.total} Gerichte für ${optionalChoiceSummary.max} ` +
        `berechtigte Teilnehmer. Bitte auf ${optionalChoiceSummary.max} reduzieren; zusätzliche Gerichte ` +
        `als kostenpflichtige Zusatzoption oder Blankoposition (7 %) erfassen.`
      );
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const bookingId = parseInt(booking.id);

      // 1. Speichere geänderte AddOns
      const modifiedAddons = bookingAddons.filter(addon => addon.isModified);
      for (const addon of modifiedAddons) {
        await adminService.updateBookingAddon(addon.id, {
          quantity: addon.quantity,
          specialRequests: addon.specialRequests
        });
      }

      // 2. Status aktualisieren (wenn geändert)
      const statusChanged = bookingStatus !== booking.status;
      const stichtagChanged = optionValidUntil !== (booking.quoteValidUntil?.split('T')[0] || '');

      if (statusChanged || (bookingStatus === 'Option' && stichtagChanged)) {
        await adminService.updateBookingStatus(
          bookingId,
          bookingStatus,
          bookingStatus === 'Option' ? optionValidUntil : undefined
        );
      }

      // 3. Teilnehmerzahl/Kategorien aktualisieren (wenn geändert)
      const originalCats = deriveCategories(booking);
      const categoriesChanged =
        categories.adults !== originalCats.adults ||
        categories.children6to12 !== originalCats.children6to12 ||
        categories.children3to5 !== originalCats.children3to5 ||
        categories.children0to2 !== originalCats.children0to2 ||
        categories.freeSeats !== originalCats.freeSeats;

      // Anzahlungssatz-Übersteuerung: Prozent → Dezimal, '' = zurücksetzen (0)
      const originalDepositPct = booking.depositPercentageOverride
        ? String(Math.round(booking.depositPercentageOverride * 100)) : '';
      const depositChanged = depositOverridePct !== originalDepositPct;
      const parsedDepositPct = parseInt(depositOverridePct);
      const depositOverrideDecimal = depositOverridePct === '' || isNaN(parsedDepositPct)
        ? 0 // 0 = Übersteuerung entfernen
        : Math.min(99, Math.max(1, parsedDepositPct)) / 100;

      if (participantCount !== booking.participantCount || categoriesChanged || depositChanged) {
        await adminService.updateAdminBooking(bookingId, {
          numberOfGuests: participantCount,
          mealSelections: [],
          selectedExtraIds: [],
          totalAmount: totalPrice,
          // Teilnehmer-Kategorien (Anlage v1.3 Abschnitt 2.7 Punkt 2)
          adultCount: categories.adults,
          childCount: categories.children6to12,
          childSmallCount: categories.children3to5,
          infantCount: categories.children0to2,
          freeParticipantWithSeatCount: categories.freeSeats,
          freeParticipantNoSeatCount: 0,
          // Anzahlungssatz nur mitschicken, wenn geändert (undefined = keine Änderung)
          ...(depositChanged ? { depositPercentageOverride: depositOverrideDecimal } : {})
        });
      }

      // Update local booking object
      const updatedBooking: Booking = {
        ...booking,
        participantCount,
        status: bookingStatus,
        quoteValidUntil: bookingStatus === 'Option' ? optionValidUntil : undefined,
        totalAmount: totalPrice
      };

      onSave(updatedBooking);
      onClose();

      alert('Buchung erfolgreich aktualisiert!');
    } catch (err: any) {
      console.error('Error updating booking:', err);
      setError(err.response?.data?.message || err.response?.data?.error || 'Fehler beim Speichern der Änderungen');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content booking-edit-dialog" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h3>Buchung bearbeiten - {booking.bookingNumber}</h3>
          <button onClick={onClose} className="btn-close" disabled={saving}>
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {/* Error Display */}
          {error && (
            <div className="alert alert-error">
              <ExclamationTriangleIcon className="w-5 h-5" />
              {error}
            </div>
          )}
          
          {/* Warning Display */}
          {warning && (
            <div className="alert alert-warning">
              <ExclamationTriangleIcon className="w-5 h-5" />
              {warning}
            </div>
          )}

          {/* Booking Info Summary */}
          <div className="booking-summary">
            <div className="summary-item">
              <strong>Kunde:</strong> {booking.customerName}
            </div>
            <div className="summary-item">
              <strong>Termin:</strong> {new Date(booking.tripDate).toLocaleDateString('de-DE')} um {booking.tripTime} Uhr
            </div>
            <div className="summary-item">
              <strong>Tour:</strong> {booking.tripType}
            </div>
          </div>

          {/* Booking Status */}
          <div className="form-section">
            <label className="form-label">Buchungsstatus</label>
            <select
              value={bookingStatus}
              onChange={(e) => setBookingStatus(e.target.value)}
              className="form-select"
              disabled={saving}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
            >
              {BOOKING_STATUS_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {/* Stichtag-Datepicker für Options */}
            {bookingStatus === 'Option' && (
              <div style={{ marginTop: '0.75rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: '#666' }}>
                  Stichtag (Option gültig bis) <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="date"
                  value={optionValidUntil}
                  onChange={(e) => setOptionValidUntil(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                  disabled={saving}
                  required
                />
                <small style={{ color: '#666', display: 'block', marginTop: '0.25rem' }}>
                  Bis zu diesem Datum muss die Option in eine Buchung umgewandelt werden.
                </small>
              </div>
            )}

            {/* Info wenn Option zu Buchung umgewandelt wird */}
            {booking.status === 'Option' && bookingStatus !== 'Option' && bookingStatus !== 'Cancelled' && (
              <div style={{ marginTop: '0.5rem', padding: '0.5rem', backgroundColor: '#f0fdf4', borderRadius: '4px', border: '1px solid #16a34a' }}>
                <small style={{ color: '#16a34a' }}>
                  ✓ Die Option wird in eine verbindliche Buchung umgewandelt.
                </small>
              </div>
            )}
          </div>

          {/* Participant Count */}
          <div className="form-section">
            <label className="form-label">
              <UserGroupIcon className="w-5 h-5" />
              Teilnehmerzahl
            </label>
            <div className="participant-control">
              <button 
                onClick={() => handleParticipantCountChange(participantCount - 1)}
                disabled={participantCount <= 1 || saving}
                className="btn-control"
              >
                -
              </button>
              <input
                type="number"
                value={participantCount}
                onChange={(e) => handleParticipantCountChange(parseInt(e.target.value) || 0)}
                className="participant-input"
                min="1"
                max="200"
                disabled={saving}
              />
              <button 
                onClick={() => handleParticipantCountChange(participantCount + 1)}
                disabled={saving}
                className="btn-control"
              >
                +
              </button>
            </div>
            {availableCapacity !== null && (
              <div className="capacity-info">
                <span className={availableCapacity > 20 ? 'text-success' : 'text-warning'}>
                  {availableCapacity} Plätze noch verfügbar
                </span>
              </div>
            )}
            {participantCount !== booking.participantCount && (
              <div className="change-info">
                <span className={participantCount > booking.participantCount ? 'text-success' : 'text-warning'}>
                  {participantCount > booking.participantCount ? '+' : ''}{participantCount - booking.participantCount} Personen
                </span>
              </div>
            )}

            {/* Teilnehmer-Kategorien (Anlage v1.3 Abschnitt 2.7 Punkt 2) */}
            <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#eef4fb', borderRadius: '8px' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                Teilnehmer-Kategorien (Staffelpreis richtet sich NUR nach Erwachsenen):
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.5rem' }}>
                {([
                  { key: 'adults' as const, label: 'Erwachsene (ab 13)' },
                  { key: 'children6to12' as const, label: 'Kinder 6-12 (60%)' },
                  { key: 'children3to5' as const, label: 'Kinder 3-5 (20%)' },
                  { key: 'children0to2' as const, label: 'Kinder 0-2 (frei, ohne Sitzplatz)' },
                  { key: 'freeSeats' as const, label: 'Freiplätze (frei)' },
                ]).map(({ key, label }) => (
                  <div key={key}>
                    <label style={{ fontSize: '0.78rem', display: 'block' }}>{label}</label>
                    <input
                      type="number"
                      min="0"
                      max="200"
                      value={categories[key]}
                      onChange={(e) => handleCategoryChange(key, parseInt(e.target.value) || 0)}
                      disabled={saving}
                      style={{ width: '100%' }}
                    />
                  </div>
                ))}
              </div>
              {tourTypesWithPricing.length === 0 && (
                <div style={{ fontSize: '0.75rem', color: '#b45309', marginTop: '0.5rem' }}>
                  Hinweis: Staffelpreise konnten nicht geladen werden, der Basispreis wird bei Kategorie-Änderungen nicht automatisch neu berechnet.
                </div>
              )}
            </div>

            {/* Anzahlungssatz je Buchung (Anlage v1.3 Abschnitt 2.5 Punkt 2) */}
            <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#fef9ec', borderRadius: '8px' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.35rem' }}>
                Anzahlungssatz für diese Buchung (%)
              </label>
              <input
                type="number"
                min="1"
                max="99"
                placeholder="Standard (Tour-Typ: 20% bzw. Exklusiv 40%)"
                value={depositOverridePct}
                onChange={(e) => setDepositOverridePct(e.target.value)}
                disabled={saving}
                style={{ width: '100%' }}
              />
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.35rem' }}>
                Leer lassen für den Standardsatz des Tour-Typs. Gilt für künftig erstellte Anzahlungsrechnungen.
              </div>
            </div>

            {/* Blankopositionen (Anlage v1.3 Abschnitt 2.5 Punkt 8) */}
            <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#f0fdf4', borderRadius: '8px' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                Blankopositionen (freie Rechnungspositionen)
              </div>

              {customItems.length > 0 && (
                <div style={{ marginBottom: '0.75rem' }}>
                  {customItems.map(item => (
                    <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0', borderBottom: '1px solid #dcfce7', fontSize: '0.85rem' }}>
                      <span style={{ flex: 1 }}>{item.description}</span>
                      <span>{item.quantity} × {formatCurrency(item.grossPrice)}</span>
                      <span style={{ color: '#6b7280' }}>MwSt {Math.round(item.taxRate * 100)}%</span>
                      <span style={{ fontWeight: 600 }}>{formatCurrency(item.grossPrice * item.quantity)}</span>
                      <button
                        type="button"
                        onClick={() => handleDeleteCustomItem(item)}
                        disabled={saving || savingCustomItem}
                        title="Entfernen"
                        style={{ border: 'none', background: 'transparent', color: '#dc2626', cursor: 'pointer', fontSize: '1rem' }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '2fr 70px 100px 110px auto', gap: '0.5rem', alignItems: 'end' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', display: 'block' }}>Text (z.B. Dekoration)</label>
                  <input type="text" value={newItemText} maxLength={500}
                    onChange={(e) => setNewItemText(e.target.value)}
                    disabled={saving || savingCustomItem} style={{ width: '100%' }} />
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', display: 'block' }}>Menge</label>
                  <input type="number" min="1" value={newItemQty}
                    onChange={(e) => setNewItemQty(e.target.value)}
                    disabled={saving || savingCustomItem} style={{ width: '100%' }} />
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', display: 'block' }}>Preis (brutto)</label>
                  <input type="text" placeholder="z.B. 100,00" value={newItemPrice}
                    onChange={(e) => setNewItemPrice(e.target.value)}
                    disabled={saving || savingCustomItem} style={{ width: '100%' }} />
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', display: 'block' }}>MwSt</label>
                  <select value={newItemTaxRate}
                    onChange={(e) => setNewItemTaxRate(e.target.value)}
                    disabled={saving || savingCustomItem} style={{ width: '100%' }}>
                    <option value="0.19">19%</option>
                    <option value="0.07">7%</option>
                    <option value="0">0%</option>
                  </select>
                </div>
                <button
                  type="button"
                  onClick={handleAddCustomItem}
                  disabled={saving || savingCustomItem}
                  style={{ padding: '0.4rem 0.8rem', background: '#16a34a', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                >
                  {savingCustomItem ? '...' : 'Hinzufügen'}
                </button>
              </div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.35rem' }}>
                Wird sofort gespeichert, erhöht den Buchungsgesamtpreis und erscheint auf künftigen Rechnungen.
              </div>
            </div>
          </div>

          {/* AddOns - Kategorisiert */}
          <div className="form-section">
            <div className="addon-header">
              <label className="form-label">Zusatzleistungen</label>
              <button
                type="button"
                onClick={loadAvailableAddons}
                disabled={saving || loadingAvailableAddons}
                className="btn-add-addon"
              >
                <PlusCircleIcon className="w-5 h-5" />
                {loadingAvailableAddons ? 'Laden...' : 'Weitere Zusatzoption'}
              </button>
            </div>

            {loadingAddons && (
              <div className="addon-loading">
                <ArrowPathIcon className="w-8 h-8 animate-spin" />
              </div>
            )}

            {addonsError && (
              <div className="addon-error">
                <ExclamationTriangleIcon className="w-5 h-5" />
                <span>{addonsError}</span>
              </div>
            )}

            {!loadingAddons && !addonsError && bookingAddons.length === 0 && (
              <div className="addon-empty">
                <span>Keine Zusatzleistungen für diese Buchung</span>
              </div>
            )}

            {!loadingAddons && bookingAddons.length > 0 && (
              <>
                {/* Sektion 1: Auswahl pro Teilnehmer */}
                {categorizedAddons.optionalChoice.length > 0 && (
                  <div className="addon-section optional-choice">
                    <div className="addon-section-header">
                      <span className="addon-section-icon">🎯</span>
                      <span className="addon-section-title">Auswahl pro Teilnehmer</span>
                    </div>
                    {/* T-058: Summenanzeige wie bei der Online-Buchung */}
                    <div
                      className="addon-section-hint"
                      style={{
                        padding: '6px 12px',
                        fontSize: '0.85rem',
                        borderRadius: '6px',
                        margin: '0 0 8px 0',
                        background: optionalChoiceSummary.over ? '#fee2e2' : (optionalChoiceSummary.remaining === 0 ? '#dcfce7' : '#fef9c3'),
                        color: optionalChoiceSummary.over ? '#991b1b' : (optionalChoiceSummary.remaining === 0 ? '#166534' : '#854d0e')
                      }}
                    >
                      {optionalChoiceSummary.over
                        ? `${optionalChoiceSummary.total} von ${optionalChoiceSummary.max} Gerichten: ${optionalChoiceSummary.total - optionalChoiceSummary.max} zu viel. Bitte reduzieren, zusätzliche Gerichte als kostenpflichtige Zusatzoption oder Blankoposition (7 %) erfassen.`
                        : optionalChoiceSummary.remaining === 0
                          ? `${optionalChoiceSummary.total} von ${optionalChoiceSummary.max} Gerichten vergeben (1 Gericht je berechtigtem Teilnehmer, Kinder 0-2 ausgenommen).`
                          : `${optionalChoiceSummary.total} von ${optionalChoiceSummary.max} Gerichten vergeben, noch ${optionalChoiceSummary.remaining} offen.`}
                    </div>
                    <div className="addon-section-content">
                      {categorizedAddons.optionalChoice.map(addon => (
                        <AddonItem
                          key={addon.id}
                          addon={addon}
                          onQuantityChange={handleAddonQuantityChange}
                          onRemove={handleRemoveAddon}
                          formatCurrency={formatCurrency}
                          disabled={saving}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Sektion 2: Inklusivleistungen */}
                {categorizedAddons.inclusive.length > 0 && (
                  <div className="addon-section inclusive">
                    <div className="addon-section-header">
                      <CheckCircleIcon className="addon-section-icon" />
                      <span className="addon-section-title">Inklusivleistungen</span>
                    </div>
                    <div className="addon-section-content">
                      {categorizedAddons.inclusive.map(addon => (
                        <AddonItem
                          key={addon.id}
                          addon={addon}
                          onQuantityChange={handleAddonQuantityChange}
                          onRemove={handleRemoveAddon}
                          formatCurrency={formatCurrency}
                          disabled={saving}
                          showInclusive
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Sektion 3: Extras */}
                {categorizedAddons.extras.length > 0 && (
                  <div className="addon-section extras">
                    <div className="addon-section-header">
                      <PlusCircleIcon className="addon-section-icon" />
                      <span className="addon-section-title">Extras</span>
                    </div>
                    <div className="addon-section-content">
                      {categorizedAddons.extras.map(addon => (
                        <AddonItem
                          key={addon.id}
                          addon={addon}
                          onQuantityChange={handleAddonQuantityChange}
                          onRemove={handleRemoveAddon}
                          formatCurrency={formatCurrency}
                          disabled={saving}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Gesamtsumme */}
                <div className="addon-summary">
                  <span>Extras Gesamt:</span>
                  <span className="addon-total">{formatCurrency(calculateAddonTotal())}</span>
                </div>
              </>
            )}
          </div>

          {/* Dialog: Weitere Zusatzoption hinzufügen - zeigt ALLE AddOns */}
          {showAddAddonDialog && (
            <div className="addon-dialog-overlay" onClick={() => setShowAddAddonDialog(false)}>
              <div className="addon-dialog" onClick={e => e.stopPropagation()}>
                <div className="addon-dialog-header">
                  <h4>Zusatzoption hinzufügen</h4>
                  <button
                    type="button"
                    onClick={() => setShowAddAddonDialog(false)}
                    className="btn-close-dialog"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
                {/* Suchfeld */}
                <div className="addon-dialog-search">
                  <input
                    type="text"
                    placeholder="Zusatzoption suchen..."
                    value={addonSearchTerm}
                    onChange={(e) => setAddonSearchTerm(e.target.value)}
                    className="addon-search-input"
                    autoFocus
                    disabled={addingAddon}
                  />
                </div>
                {/* Fehleranzeige im Dialog */}
                {dialogError && (
                  <div className="addon-dialog-error">
                    <ExclamationTriangleIcon className="w-5 h-5" />
                    <span>{dialogError}</span>
                  </div>
                )}
                {/* Loading-Anzeige beim Hinzufügen */}
                {addingAddon && (
                  <div className="addon-dialog-loading">
                    <div className="spinner-sm"></div>
                    <span>Wird hinzugefügt...</span>
                  </div>
                )}
                <div className="addon-dialog-content">
                  {/* Zeige ALLE aktiven AddOn-Services, gefiltert nach Suchbegriff */}
                  {allAddOnServices
                    .filter(a => {
                      // Nicht bereits in Buchung
                      if (bookingAddons.some(b => b.addOnServiceId === a.id)) return false;
                      // Muss aktiv sein
                      if (!a.isActive) return false;
                      // Suchfilter
                      if (addonSearchTerm) {
                        const searchLower = addonSearchTerm.toLowerCase();
                        return (
                          a.name.toLowerCase().includes(searchLower) ||
                          (a.categoryName?.toLowerCase().includes(searchLower) || false)
                        );
                      }
                      return true;
                    })
                    .map(addon => {
                      // Prüfe ob dieses AddOn im TourType konfiguriert ist
                      const tourTypeConfig = availableAddons.find(c => c.addOnServiceId === addon.id);
                      return (
                        <button
                          key={addon.id}
                          type="button"
                          className="addon-dialog-item"
                          disabled={addingAddon}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('Button geklickt für:', addon.name, addon.id);
                            handleAddNewAddon(addon);
                          }}
                        >
                          <div className="addon-dialog-item-info">
                            <span className="addon-dialog-item-name">{addon.name}</span>
                            {addon.categoryName && (
                              <span className="addon-dialog-item-category">{addon.categoryName}</span>
                            )}
                            {addon.isOptionalChoice && (
                              <span className="addon-badge optional">Auswahl</span>
                            )}
                            {tourTypeConfig?.isIncludedInTourPrice && (
                              <span className="addon-badge inclusive">Inklusive</span>
                            )}
                          </div>
                          <span className="addon-dialog-item-price">
                            {formatCurrency(addon.grossPrice)}
                          </span>
                        </button>
                      );
                    })}
                  {allAddOnServices.filter(a => {
                    if (bookingAddons.some(b => b.addOnServiceId === a.id)) return false;
                    if (!a.isActive) return false;
                    if (addonSearchTerm) {
                      const searchLower = addonSearchTerm.toLowerCase();
                      return a.name.toLowerCase().includes(searchLower) || (a.categoryName?.toLowerCase().includes(searchLower) || false);
                    }
                    return true;
                  }).length === 0 && (
                    <div className="addon-dialog-empty">
                      {addonSearchTerm
                        ? `Keine Zusatzoptionen gefunden für "${addonSearchTerm}"`
                        : 'Alle verfügbaren Zusatzoptionen sind bereits in der Buchung enthalten.'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Price Summary */}
          <div className="price-summary">
            <div className="price-row">
              <span>Bisheriger Preis:</span>
              <span>{formatCurrency(booking.totalAmount)}</span>
            </div>
            <div className="price-row">
              <span>Neuer Preis:</span>
              <span className="new-price">{formatCurrency(totalPrice)}</span>
            </div>
            <div className={`price-row difference ${priceDifference >= 0 ? 'positive' : 'negative'}`}>
              <span>Differenz:</span>
              <span>
                {priceDifference >= 0 ? '+' : ''}{formatCurrency(priceDifference)}
              </span>
            </div>
            {booking.paidAmount > 0 && (
              <div className="price-row">
                <span>Bereits bezahlt:</span>
                <span>{formatCurrency(booking.paidAmount)}</span>
              </div>
            )}
            {priceDifference > 0 && booking.paidAmount > 0 && (
              <div className="price-row highlight">
                <span>Nachzahlung erforderlich:</span>
                <span>{formatCurrency(priceDifference)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button 
            onClick={onClose} 
            className="btn-secondary"
            disabled={saving}
          >
            Abbrechen
          </button>
          <button 
            onClick={handleSave}
            className="btn-primary"
            disabled={saving || loading}
          >
            {saving ? (
              <>
                <div className="spinner-sm"></div>
                Speichern...
              </>
            ) : (
              <>
                <CheckCircleIcon className="w-5 h-5" />
                Änderungen speichern
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingEditDialog;
